import React from 'react';
import { 
  MessageCircle, 
  Facebook, 
  Share2, 
  Linkedin, 
  Send,
  Copy,
  Instagram,
  Globe,
} from 'lucide-react';
import { toast } from '../ui/Toaster';
import { copyToClipboard } from '../../utils/sharingUtils';

export type SharingPlatform = 'whatsapp' | 'whatsapp-web' | 'facebook' | 'reddit' | 'instagram' | 'linkedin' | 'telegram' | 'copy';

interface ShareButtonProps {
  platform: SharingPlatform;
  url: string;
  title: string;
  description?: string | undefined;
  hashtags?: string[];
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

// Platform configurations
const platformConfigs = {
  whatsapp: {
    name: 'WhatsApp',
    icon: MessageCircle,
    color: 'bg-[#25D366] hover:bg-[#1DA851]',
    generateUrl: (url: string, title: string) => 
      `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  'whatsapp-web': {
    name: 'WhatsApp Web',
    icon: Globe,
    color: 'bg-[#25D366] hover:bg-[#1DA851]',
    generateUrl: (url: string, title: string) => 
      `https://web.whatsapp.com/send?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: 'bg-[#1877F2] hover:bg-[#166FE5]',
    generateUrl: (url: string, _title: string, hashtag?: string) => 
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}${hashtag ? `&hashtag=${encodeURIComponent(hashtag)}` : ''}`,
  },
  reddit: {
    name: 'Reddit',
    icon: Share2,
    color: 'bg-[#FF4500] hover:bg-[#E13D00]',
    generateUrl: (url: string, title: string) => 
      `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'bg-[#0077B5] hover:bg-[#005885]',
    generateUrl: (url: string, title: string, summary?: string) => 
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}${title ? `&title=${encodeURIComponent(title)}` : ''}${summary ? `&summary=${encodeURIComponent(summary)}` : ''}`,
  },
  telegram: {
    name: 'Telegram',
    icon: Send,
    color: 'bg-[#0088CC] hover:bg-[#0077B3]',
    generateUrl: (url: string, title: string) => 
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'bg-gradient-to-r from-[#E4405F] to-[#F77737] hover:from-[#D73652] to-[#E6692E]',
    generateUrl: () => '', // Instagram doesn't support direct URL sharing
  },
  copy: {
    name: 'Link kopiëren',
    icon: Copy,
    color: 'bg-white/[.08] border border-white/10 hover:bg-white/[.14]',
    generateUrl: () => '',
  }
};

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12'
};

const iconSizes = {
  sm: 16,
  md: 20,
  lg: 24
};

const ShareButton: React.FC<ShareButtonProps> = ({
  platform,
  url,
  title: _title,
  description,
  hashtags = [],
  className = '',
  size = 'md'
}) => {
  const config = platformConfigs[platform];
  const Icon = config.icon;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (platform === 'copy') {
      const success = await copyToClipboard(url);
      if (success) {
        toast.success('Succes', 'Link gekopieerd naar klembord!');
      } else {
        toast.error('Fout', 'Kon link niet kopiëren');
      }
      return;
    }

    if (platform === 'instagram') {
      const success = await copyToClipboard(url);
      if (success) {
        toast.info('Instagram', 'Link gekopieerd! Plak de link in je Instagram Story.');
      } else {
        toast.error('Fout', 'Kon link niet kopiëren');
      }
      return;
    }

    // Generate platform-specific URL
    let shareUrl = '';
    
    switch (platform) {
      case 'whatsapp':
      case 'whatsapp-web':
        shareUrl = config.generateUrl(url, _title);
        break;
      case 'facebook':
        shareUrl = config.generateUrl(url, _title, hashtags.length > 0 ? `#${hashtags[0]}` : undefined);
        break;
      case 'reddit':
        shareUrl = config.generateUrl(url, _title);
        break;
      case 'linkedin':
        shareUrl = config.generateUrl(url, _title, description);
        break;
      case 'telegram':
        shareUrl = config.generateUrl(url, _title);
        break;
    }

    if (shareUrl) {
      // WhatsApp Web: open in normal tab, others in popup
      if (platform === 'whatsapp-web') {
        // Open WhatsApp Web in a normal browser tab
        window.open(shareUrl, '_blank');
      } else {
        window.open(shareUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
      }
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`
        ${sizeClasses[size]}
        ${config.color}
        text-white
        rounded-item
        flex
        items-center
        justify-center
        transition-all
        duration-200
        hover:scale-105
        focus:outline-none
        focus-visible:ring-2
        focus-visible:ring-neon-pink/50
        ${className}
      `}
      title={`Delen via ${config.name}`}
      aria-label={`Delen via ${config.name}`}
    >
      <Icon size={iconSizes[size]} />
    </button>
  );
};

export default ShareButton;